import React, { useState } from 'react';
import {
  Calendar,
  MapPin,
  Clock,
  Ticket,
  X,
  ShoppingCart,
  ArrowLeft,
  Globe,
  Zap,
  Users,
} from 'lucide-react';
import './Tour.css';
import usePageTitle from '../hooks/usePageTitle';
import NewsletterSignup from '../components/NewsletterSignup';
import LatestNews from '../components/LatestNews';

interface TicketType {
  id: string;
  name: string;
  price: number;
  available: boolean;
}

interface Show {
  id: number;
  date: string;
  venue: string;
  city: string;
  time?: string;
  status: string;
  ticketTypes?: TicketType[];
}

const launchShow = {
  eyebrow: 'World Premiere',
  title: 'The Debut',
  subtitle: 'The night the world meets THE KULTS',
  date: 'Late 2026',
  dateNote: 'Exact date to be announced',
  venue: 'Secret Underground Venue',
  city: 'Location revealed to insiders only',
  status: 'Announcing Soon',
  capacity: 'Strictly limited capacity',
};

const marqueeItems = [
  'THE KULTS',
  'DEBUT SHOW',
  'LATE 2026',
  'UNDERGROUND',
  'INSIDERS ONLY',
];

const Tour: React.FC = () => {
  usePageTitle('Tour - The Kults');
  const [selectedShow, setSelectedShow] = useState<Show | null>(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [selectedTicketType, setSelectedTicketType] = useState<string>('');
  const [showTicketModal, setShowTicketModal] = useState(false);

  // No additional confirmed dates yet — the debut leads the campaign.
  const upcomingShows: Show[] = [];

  const handleGetTickets = (show: Show): void => {
    setSelectedShow(show);
    setShowTicketModal(true);
    setTicketQuantity(1);
    setSelectedTicketType('');
  };

  const handleCloseModal = (): void => {
    setShowTicketModal(false);
    setSelectedShow(null);
  };

  const availableTicketTypes: TicketType[] =
    selectedShow?.ticketTypes?.filter((t) => t.available) ?? [];

  const handleAddToCart = (): void => {
    if (!selectedTicketType) return;

    const selectedType = availableTicketTypes.find(
      (t) => t.id === selectedTicketType
    );
    if (!selectedType) return;

    alert(`Added ${ticketQuantity} ${selectedType.name} ticket(s) to cart!`);
    handleCloseModal();
  };

  const scrollToList = (): void => {
    const el = document.getElementById('join-the-list');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="tour-page">
      <section className="tour-hero">
        <div className="container">
          <span className="tour-hero-eyebrow">Live</span>
          <h1>TOUR</h1>
          <p>The ritual begins. Be in the room when it does.</p>
        </div>
      </section>

      {/* Debut launch centerpiece */}
      <section className="launch-section" aria-labelledby="launch-title">
        <div className="launch-marquee" aria-hidden="true">
          <div className="launch-marquee-track">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map(
              (item, index) => (
                <span key={index} className="launch-marquee-item">
                  {item}
                  <span className="launch-marquee-dot">◆</span>
                </span>
              )
            )}
          </div>
        </div>

        <div className="container">
          <div className="launch-card">
            <div className="launch-card-header">
              <span className="launch-eyebrow">
                <Zap size={14} />
                {launchShow.eyebrow}
              </span>
              <span className="launch-status">
                <span className="launch-status-pulse" />
                {launchShow.status}
              </span>
            </div>

            <h2 id="launch-title" className="launch-title">
              {launchShow.title}
            </h2>
            <p className="launch-subtitle">{launchShow.subtitle}</p>

            <div className="launch-meta">
              <div className="launch-meta-item">
                <Calendar size={22} />
                <div>
                  <span className="launch-meta-label">When</span>
                  <span className="launch-meta-value">{launchShow.date}</span>
                  <span className="launch-meta-note">{launchShow.dateNote}</span>
                </div>
              </div>
              <div className="launch-meta-item">
                <MapPin size={22} />
                <div>
                  <span className="launch-meta-label">Where</span>
                  <span className="launch-meta-value">{launchShow.venue}</span>
                  <span className="launch-meta-note">{launchShow.city}</span>
                </div>
              </div>
              <div className="launch-meta-item">
                <Users size={22} />
                <div>
                  <span className="launch-meta-label">Access</span>
                  <span className="launch-meta-value">{launchShow.capacity}</span>
                  <span className="launch-meta-note">No second chances</span>
                </div>
              </div>
            </div>

            <p className="launch-pitch">
              One night. One room. The Kults take the stage for the very first
              time — an intimate, underground baptism marking the moment the
              world meets the band. When the doors open, everything changes.
              Get on the list to unlock the date, the city, and first access
              before anyone else.
            </p>

            <div className="launch-actions">
              <button
                type="button"
                className="launch-btn primary"
                onClick={scrollToList}
              >
                <Ticket size={18} />
                Get on the List
              </button>
              <a
                href="/music"
                className="launch-btn secondary"
              >
                <Globe size={18} />
                Hear the Sound
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Additional dates / empty state */}
      <section className="upcoming-shows">
        <div className="container">
          <h2>More Dates</h2>
          {upcomingShows.length === 0 ? (
            <div className="no-shows">
              <div className="no-shows-content">
                <Calendar size={48} />
                <h3>The Tour Is Loading</h3>
                <p>
                  The full run of dates drops after the debut. Insiders hear it
                  first — lock in your spot for presale access and the location
                  reveal.
                </p>
                <div id="join-the-list">
                  <NewsletterSignup
                    title="Join the Inner Circle"
                    description="Be first to get the debut date, venue reveal, and exclusive presale access before tickets go public."
                    variant="compact"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="shows-grid">
              {upcomingShows.map((show) => (
                <div key={show.id} className="show-card">
                  <div className="show-date">
                    <Calendar size={20} />
                    <span>{show.date}</span>
                  </div>
                  <div className="show-venue">
                    <MapPin size={20} />
                    <span>{show.venue}</span>
                  </div>
                  <div className="show-city">{show.city}</div>
                  <div className="show-time">
                    <Clock size={16} />
                    <span>{show.time}</span>
                  </div>
                  <div
                    className={`show-status ${show.status
                      .toLowerCase()
                      .replace(' ', '-')}`}
                  >
                    {show.status}
                  </div>
                  <button
                    className="ticket-button"
                    onClick={() => handleGetTickets(show)}
                    disabled={show.status === 'Sold Out'}
                  >
                    <Ticket size={16} />
                    {show.status === 'Sold Out' ? 'Sold Out' : 'Get Tickets'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ticket Purchase Modal */}
      {showTicketModal && selectedShow && (
        <div className="ticket-modal" onClick={handleCloseModal}>
          <button
            className="close-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            aria-label="Close modal"
          >
            <X />
          </button>
          <div
            className="ticket-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ticket-modal-header">
              <button className="back-btn" onClick={handleCloseModal}>
                <ArrowLeft size={20} />
                Back to Shows
              </button>
              <h2>Get Tickets</h2>
            </div>

            <div className="show-details">
              <h3>{selectedShow.venue}</h3>
              <p className="show-info">
                <Calendar size={16} />
                {selectedShow.date} at {selectedShow.time}
              </p>
              <p className="show-location">
                <MapPin size={16} />
                {selectedShow.city}
              </p>
            </div>

            <div className="ticket-selection">
              <h3>Select Ticket Type</h3>
              <div className="ticket-types">
                {availableTicketTypes.map((ticketType) => (
                  <button
                    key={ticketType.id}
                    className={`ticket-type-btn ${
                      selectedTicketType === ticketType.id ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedTicketType(ticketType.id)}
                  >
                    <div className="ticket-type-info">
                      <h4>{ticketType.name}</h4>
                      <span className="ticket-price">${ticketType.price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedTicketType && (
              <>
                <div className="quantity-selection">
                  <h3>Quantity</h3>
                  <div className="quantity-controls">
                    <button
                      className="qty-btn"
                      onClick={() =>
                        setTicketQuantity(Math.max(1, ticketQuantity - 1))
                      }
                      disabled={ticketQuantity <= 1}
                    >
                      -
                    </button>
                    <span className="quantity">{ticketQuantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => setTicketQuantity(ticketQuantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="ticket-summary">
                  <div className="summary-item">
                    <span>Ticket Price:</span>
                    <span>
                      $
                      {
                        availableTicketTypes.find(
                          (t) => t.id === selectedTicketType
                        )?.price
                      }
                    </span>
                  </div>
                  <div className="summary-item">
                    <span>Quantity:</span>
                    <span>{ticketQuantity}</span>
                  </div>
                  <div className="summary-item total">
                    <span>Total:</span>
                    <span>
                      $
                      {(
                        (availableTicketTypes.find(
                          (t) => t.id === selectedTicketType
                        )?.price ?? 0) * ticketQuantity
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                <button className="add-to-cart-btn" onClick={handleAddToCart}>
                  <ShoppingCart size={20} />
                  Add to Cart
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <LatestNews maxArticles={3} />
    </div>
  );
};

export default Tour;
